/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import isEmpty from "lodash.isempty";
import get from "lodash.get";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import Typography from "../../components/atoms/typography";
import { labelStyle, constants, productTypes, metrics } from "../../constants/pageConstants/pdpConstants";
import { LOCAL_CURRENCY } from "../../constants/common/common";
import { setCatalogId, setIsMobile } from "../../store/slices/common/appConfig";
import { getLocalStorageItem, setLocalStorageItem } from "../../utils/storage/localStorageUtil";
import CookieUtil from "../../utils/storage/cookieUtil";
import {
  requestToFetchProductStockAvailable,
  responseFromFetchProductStockAvailable,
  setClientCurrentClickedItem,
  setClientVariantsLabel,
  setClientVariantType,
  setDeliveryType,
  setFullProductDetails,
  setClientFlavorType,
  setFlavorListForCakeTypeProduct,
} from "../../store/slices/pdpSlice";
import {
  checkIsInternationalProduct,
  getOutOfStock,
  isNonDeliverable,
  isNonDeliverableAbroad,
  isSameSkuProduct,
} from "../../utils/pdp/nonDeliverable";
import { setProductDeliveryShippingDetailsAction } from "../../store/slices/common/shippingDateAndSlotSelection";
import useAnalytics from "../../analytics/useAnalytics";
import { borderLine, checkIsDigitalProduct, generateFlavorTypeList } from "../../utils/pdp/common";
import { setSelectedDomesticLocation } from "../../store/slices/locationLock";
import { campaignEvents, cleverTapCommonEvents, duplicateCleverTapEvents } from "../../analytics/clevertapConstants";
import { nsaOrOutOfStockGaEvent, setGA4PdpPageGaEvent } from "../../analytics/gaEvents";
import { LAST_VISITED_PINCODE, SELECTED_INTERNATIONAL_CITY } from "../../constants/storage";
import { GA4_EVENTS } from "../../analytics/gaConstant";
import { PageTypes, setPageType } from "../../utils/fnpPageType";
import useCleverTapPDPData from "../../customHooks/clevertap/pdp";
import usePDPOmnitureSetup from "../../customHooks/omniture/usePDPOmnitureSetup";
import ProductDisplayGallery from "../../components/molecules/productDisplayGallery";
import ProductPriceSection from "../../components/molecules/pdpProductPriceSection";
import OffersCard from "../../components/molecules/offersCard";
import ComboComponents from "../../components/molecules/comboComponents";
import AccordionSection from "./productDetailComponents/AccordionSection";
import Breadcrumb from "../../components/atoms/breadCrumb"; // make skeleton and then make it dynamic import
import HiddenInputs from "./productDetailComponents/pdpHiddenInputs";
import getFnpSalesChannelInfo from "../../utils/fnpChannelsInfo";
import Skeleton from "../../components/molecules/skeletons/skeleton";
import { isJsonString, removeEmptyNullAndUndefinedFromObject, restrictAutoScrollingOnLoad } from "../../utils/common";
import ExploreMore from "../../components/organisms/exploreMore";
import WebNativeDisplayBanners from "./productDetailComponents/WebNativeDisplayBanners";

const CommonConditionalComponents = dynamic(() => import("../../components/molecules/commonConditionalComponents"), {
  ssr: false,
});
const ZoomGallery = dynamic(() => import("../../components/molecules/zoomGallery"), {
  ssr: false,
});
const TrustMetrics = dynamic(() => import("../../components/molecules/trustMetrics"), {
  ssr: false,
});
const OneTapLogin = dynamic(() => import("../../components/organisms/googleOneTapLogin"), {
  ssr: false,
});
const DesktopLocationLockSection = dynamic(() => import("./productDetailComponents/desktopLocationLockSection"), {
  loading: () => <Skeleton customStyle={{ width: "100%", height: "50px" }} />,
});
const MobileLocationLockSection = dynamic(() => import("./productDetailComponents/mobileLocationLockSection"), {
  loading: () => <Skeleton customStyle={{ width: "100%", height: "50px" }} />,
});
const DeliveryDateAndtimeSlotSelection = dynamic(
  () => import("./productDetailComponents/deliveryDateAndtimeSlotSelectionSection"),
  {
    loading: () => <Skeleton customStyle={{ width: "100%", height: "50px" }} />,
  },
);
const ChooseDeliveryType = dynamic(() => import("./productDetailComponents/ChooseDeliveryType"), {
  loading: () => <Skeleton customStyle={{ width: "100%", height: "50px" }} />,
});

const Loader = dynamic(() => import("../../components/atoms/loader"));
const ProductDetailWidgetSection = dynamic(() => import("./productDetailComponents/productDetailWidgetSection"));
const BuyNowSection = dynamic(() => import("./productDetailComponents/buyNowSection"), { ssr: false });
const PersonalizedOptionSection = dynamic(() => import("./productDetailComponents/personalizedOptionSection"));

/**
 * ProductDetailPage container that displays a product gallery.
 *
 * This container renders the ProductDisplayGallery with specific properties
 * tailored for mobile view. It uses constants from pdpConstants for the data
 * and rating information.
 *
 * @returns {JSX.Element} A React component that renders the product gallery.
 */
const ProductDetailPage = ({
  serverData,
  serverOffersData,
  serverVariantProducts,
  serverVariantLabel,
  serverVariantType,
  isMobile,
  fullProductDetails,
  serverSelectedCurrency,
  breadCrumb,
  catalogId,
  productStockAvailable,
  serverVariantFlavor,
  pdpFooterLinks = {},
  serverCustomerReviewData,
}) => {
  const dispatch = useDispatch();
  const { init, trackEvent } = useAnalytics();
  const {
    selectedCurrencyFromState,
    clientCurrentClickedItem,
    deliveryDetails,
    deliveryDates,
    shippingDetails,
    isLoading,
    deliveryType,
    shippingDetailsInternational,
    isUserLoggedIn,
    pincode,
    selectedIntLocation,
    requestToAddToCart,
    clientVariantLabel,
    deliveryDetailsData,
    userPersonalization,
    isCartVisible,
  } = useSelector(
    (state) => ({
      selectedCurrencyFromState: state?.currencyList?.userSelectedCurrency,
      clientCurrentClickedItem: state?.pdp?.clientCurrentClickedItem,
      deliveryDetails: state?.shippingDateAndSlotSelection,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      shippingDetails: state?.shippingDateAndSlotSelection?.shippingDetails,
      isLoading: state?.shippingDateAndSlotSelection?.loading,
      deliveryType: state?.pdp?.deliveryType,
      shippingDetailsInternational: state?.shippingDateAndSlotSelection?.internationalDeliveryMethods,
      isUserLoggedIn: state?.fus?.loginDetail?.loggedin,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      selectedIntLocation: state?.locationLock.selectedIntLocation,
      requestToAddToCart: state?.cart?.requestToAddToCart,
      clientVariantLabel: state?.pdp?.clientVariantLabel,
      deliveryDetailsData: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      userPersonalization: state?.personalization?.userPersonalization,
      isCartVisible: state?.cart?.isCartVisible,
    }),
    shallowEqual,
  );
  const initialRender = useRef(false);
  const [bannerData, setBannerData] = useState(null);
  const [campaignEventData, setCampaignEventData] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState(serverSelectedCurrency || {});
  const currentItem = isEmpty(clientCurrentClickedItem) ? serverData : clientCurrentClickedItem;
  const { primaryCategoryId = "", productId = "", assocProductDetails = null, productTypeId = "" } = currentItem || {};
  const isSameSku = isSameSkuProduct(assocProductDetails);
  const isInternational = checkIsInternationalProduct(primaryCategoryId);
  const [isNotServiceable, setIsNotServiceable] = useState(false);
  const isOutOfStock = getOutOfStock(currentItem?.productStockAvailable || productStockAvailable, productId) ?? true;

  const currency = CookieUtil.getCookie(LOCAL_CURRENCY);
  const lastVisitedPincode = CookieUtil.getCookie(LAST_VISITED_PINCODE);
  const selectedIntlCity = CookieUtil.getCookie(SELECTED_INTERNATIONAL_CITY);
  const selectedCountry = selectedIntlCity && JSON.parse(selectedIntlCity);
  const cityOrPincode = !isInternational ? pincode : selectedIntLocation?.pincode;
  const pageReferrer = typeof window !== "undefined" ? document.referrer : "";
  const pageCategoryURL = !isEmpty(pageReferrer) ? new URL(pageReferrer) : "";

  usePDPOmnitureSetup(breadCrumb, pageCategoryURL);

  const properties = setGA4PdpPageGaEvent(
    currentItem,
    lastVisitedPincode,
    isUserLoggedIn ? true : false,
    currency,
    fullProductDetails,
    clientVariantLabel,
    catalogId,
    selectedCountry?.[catalogId.toUpperCase()]?.city,
  );
  //loader code for msite INP issue
  // const [isPageLoaded, setIsPageLoaded] = useState(false);

  // useEffect(() => {
  //   if (typeof window === "undefined" || typeof document === "undefined") return;
  //   if (isMobile) {
  //     const handlePageLoad = () => {
  //       setIsPageLoaded(true);
  //     };

  //     if (document.readyState === "complete") {
  //       handlePageLoad();
  //     } else {
  //       window.addEventListener("load", handlePageLoad);
  //     }
  //     return () => {
  //       window.removeEventListener("load", handlePageLoad);
  //     };
  //   }
  // }, []);

  useEffect(() => {
    if (cityOrPincode && !isLoading) {
      const noService = !isInternational
        ? isNonDeliverable(deliveryDates, !!shippingDetails?.data?.length, assocProductDetails, primaryCategoryId)
        : isNonDeliverableAbroad(shippingDetailsInternational?.isShippingDetailsExists);
      setIsNotServiceable(noService);
    }
  }, [deliveryDates, shippingDetailsInternational, cityOrPincode, isLoading]);

  useEffect(() => {
    const nsaOrOutofStock = nsaOrOutOfStockGaEvent(
      pincode || lastVisitedPincode,
      !isEmpty(clientCurrentClickedItem) ? clientCurrentClickedItem : serverData,
    );
    if (isNotServiceable && (pincode || lastVisitedPincode)) {
      trackEvent(GA4_EVENTS?.NSA, { ...nsaOrOutofStock }, ["ga4"]);
    }
    if (isOutOfStock) {
      trackEvent(GA4_EVENTS?.OUT_OF_STOCK, { ...nsaOrOutofStock }, ["ga4"]);
    }
  }, [isNotServiceable, pincode, isOutOfStock]);

  const { commonCleverTapData, cleverTapPageData, cleverTapPDPEventData, duplicateEventData } = useCleverTapPDPData({
    currentItem,
    serverData,
    selectedCurrency,
    deliveryDates,
    isOutOfStock,
    isSameSku,
    catalogId,
  });
  const pdpImageViewedData = {
    pid: duplicateEventData?.pid || "",
    area: duplicateEventData?.area || "",
    city: duplicateEventData?.city || "",
    pincode: duplicateEventData?.pincode || "",
    system_pincode: duplicateEventData?.system_pincode || "",
    country: duplicateEventData?.country || "",
    sku_id: duplicateEventData?.sku_id || "",
    product_name: currentItem?.productName || "",
    is_international: isInternational,
  };

  useEffect(() => {
    if (productId && initialRender?.current) {
      dispatch(requestToFetchProductStockAvailable({ productId }));
    }
    if (checkIsDigitalProduct(currentItem)) {
      const { geoId = "", displayText = "" } = currentItem?.shippingDisplayOptions?.defaultGeoDetails || {};
      dispatch(
        setSelectedDomesticLocation({
          pincode: geoId,
          address: displayText,
        }),
      );
    }
    initialRender.current = true;
  }, [productId]);

  useEffect(() => {
    if (Object.keys(selectedCurrencyFromState).length === 0) {
      if (serverSelectedCurrency?.currency?.id) {
        CookieUtil.setCookie(LOCAL_CURRENCY, serverSelectedCurrency?.currency?.id ?? "", {
          path: "/",
          encode: decodeURIComponent,
        });
      }
    } else {
      setSelectedCurrency(selectedCurrencyFromState);
    }
  }, [selectedCurrencyFromState]);

  useEffect(() => {
    dispatch(setIsMobile(isMobile));
    dispatch(setCatalogId(catalogId));
    dispatch(setClientCurrentClickedItem(serverData));
    dispatch(setClientVariantType(serverVariantType));
    dispatch(setClientVariantsLabel(serverVariantLabel));
    dispatch(setFullProductDetails(fullProductDetails));
    if (serverVariantFlavor) {
      dispatch(setClientFlavorType(serverVariantFlavor));
      dispatch(setFlavorListForCakeTypeProduct(generateFlavorTypeList(fullProductDetails?.variantProducts)));
    }
  }, [isMobile, serverData]);

  useEffect(() => {
    /**
     * Handles the "CT_web_native_display" event and sets the appropriate state
     * based on the delivery topic.
     *
     * @param {object} event .
     */
    const handleEvent = (event) => {
      if (event.detail.kv.topic === "pdp_banner_campaign") {
        trackEvent(campaignEvents.RENDER_NOTIFICATION_VIEWED, { ...event?.detail }, ["clevertap"]);
        setCampaignEventData({ ...campaignEventData, [event.detail.msgId]: event?.detail });
        const campaignParsedData = isJsonString(event.detail.kv?.data) ? JSON.parse(event.detail.kv?.data) : [];
        campaignParsedData.map((item) => (item.campaign_id = event.detail.msgId));
        setBannerData({ ...event.detail.kv, data: campaignParsedData });
      }
    };

    document.addEventListener("CT_web_native_display", handleEvent);

    /**
     *  handlePageShow check whether page is served from bfCache
     * @param {*} event
     */
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    /**
     * set fnp page type for pdp page
     */
    setPageType(PageTypes.PRODUCT);

    restrictAutoScrollingOnLoad();
    /**
     * // Function to get unique recently viewed products
     * @returns
     */
    const getUniqueRecentlyViewedProducts = () => {
      try {
        // Retrieve the existing array from local storage
        const storedProducts = getLocalStorageItem("recentlyViewedProducts");
        const previousRecentlyViewedProducts = storedProducts ? JSON.parse(storedProducts || "[]") : [];

        // Ensure the product ID is unique before adding it
        if (currentItem?.productId && !previousRecentlyViewedProducts.includes(currentItem.productId)) {
          previousRecentlyViewedProducts.unshift(currentItem.productId);
          if (previousRecentlyViewedProducts.length > 40) {
            previousRecentlyViewedProducts.pop();
          }
        }

        return previousRecentlyViewedProducts;
      } catch (error) {
        console.error("Error accessing local storage:", error);
        return [];
      }
    };

    /**
     * Function to update local storage with unique products
     */
    const updateRecentlyViewedProducts = () => {
      const uniqueProducts = getUniqueRecentlyViewedProducts();
      setLocalStorageItem("recentlyViewedProducts", JSON.stringify(uniqueProducts));
    };

    // Update local storage with the new product ID
    updateRecentlyViewedProducts();
    dispatch(responseFromFetchProductStockAvailable(productStockAvailable));
    // Initialize analytic tools
    init();
    trackEvent(GA4_EVENTS?.VIEW_ITEM, { ...properties }, ["ga4"]);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("CT_web_native_display", handleEvent);
    };
  }, []);

  useEffect(() => {
    if (selectedCurrency && isUserLoggedIn !== undefined && !isCartVisible) {
      const filteredDuplicateEventData = removeEmptyNullAndUndefinedFromObject(duplicateEventData);

      trackEvent(cleverTapCommonEvents.pdpPageViewed, cleverTapPageData, ["clevertap"]);

      duplicateCleverTapEvents?.[PageTypes.PRODUCT]?.forEach((eventName) => {
        trackEvent(eventName, filteredDuplicateEventData, ["clevertap"]);
      });
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    dispatch(setDeliveryType(primaryCategoryId));
    if (deliveryDetails?.[productId]) {
      dispatch(
        setProductDeliveryShippingDetailsAction({
          deliveryDates: deliveryDetails?.[productId]?.deliveryDates,
          shippingDetails: deliveryDetails?.[productId]?.shippingDetails,
          nextAvailableDifference: deliveryDetails?.[productId]?.nextAvailableDifference,
        }),
      );
    }
  }, [primaryCategoryId, deliveryDetails?.[productId]]);

  /** */
  const onGa4EventBuyNow_AddToCart = (eventType) => {
    trackEvent(eventType, { ...properties }, ["ga4"]);
  };

  /**
   * Function to handle the addition of a product video link to the product images.
   *
   * This function takes in product value and checks if a product video link is present.
   * If a video link exists, it appends the last image to the list of images and returns
   * the updated list. Otherwise, it simply returns the original list of product images.
   * @returns {Array} An array of product images, possibly including an additional image if a video link exists.
   */
  const getProductImages = (value) => {
    let productImages = get(value, "productContent.productImages");
    const hasPersonalizableUI = get(value, "hasPersonalizableUI");
    if (hasPersonalizableUI) {
      if (userPersonalization && userPersonalization.length > 0 && userPersonalization[0]?.previewImage) {
        productImages = [userPersonalization[0]?.previewImage, ...productImages];
      } else if (userPersonalization && userPersonalization.length > 0 && userPersonalization[0]?.value?.length > 0) {
        productImages = [userPersonalization[0]?.value[0], ...productImages];
      }
    }
    return productImages;
  };

  const containerPadding = "px-12 md:px-0";

  /**
   * Creates breadcrumb data based on the provided input.
   *
   * @param {Object} data - The data used to generate breadcrumbs.
   * @param {Object} data.data - The nested data object containing details.
   * @param {string} data.data.primaryCategoryId - The primary category ID, used for generating a category URL.
   * @param {string} data.data.productName - The product name, used for the final breadcrumb.
   * @returns {Array<Object>} An array of breadcrumb objects with `label` and `url` properties.
   */
  const createBreadcrumbsData = (data) => {
    const breadcrumbs = [
      {
        label: "Home",
        url: "/",
      },
    ];

    if (data?.primaryCategoryId) {
      breadcrumbs.push({
        label: data.primaryCategoryId.split("-").join(" "),
        url: `/${data.primaryCategoryId}-lp`,
      });
    }

    if (data?.productName) {
      breadcrumbs.push({
        label: data.productName,
        url: "",
      });
    }

    return breadcrumbs;
  };

  return (
    <div id="mainContent">
      {/* loader code for msite INP issue */}
      {/* {((isMobile && !isPageLoaded) || requestToAddToCart) && <Loader />} */}
      {requestToAddToCart && <Loader />}

      <OneTapLogin />
      <div className="relative flex w-full flex-col justify-between border-b-4 border-lightGrey pb-10 pt-10 md:flex-row md:gap-5 md:px-[112px] md:pb-[40px] md:pt-0 xll:px-[160px]">
        <div
          className={`z-1 w-full self-start ${containerPadding} md:sticky md:top-148 md:w-[53.3%] lg:w-2/4`}
          id="productGallery"
        >
          {!isMobile && <Breadcrumb items={createBreadcrumbsData(breadCrumb)} catalogId={catalogId} />}
          <ProductDisplayGallery
            productImages={getProductImages(currentItem)}
            productReviewsAndRatings={get(currentItem, "productReviewsAndRatings")}
            productVideoLink={get(currentItem, "productContent.productVideoLink")}
            urlIdentifier={serverData?.urlIdentifier}
            skuCode={get(currentItem, "skuCode")}
            isMobile={isMobile}
            ctImageViewedData={pdpImageViewedData}
          />
        </div>

        <div
          className="relative mt-32 flex w-full flex-col gap-18 md:mt-0  md:w-[46.5%] md:pt-60"
          id="productInformation"
        >
          <ProductPriceSection
            containerPadding={containerPadding}
            currentItem={currentItem}
            selectedCurrency={selectedCurrency}
            isMobile={isMobile}
            serverData={serverData}
            isOutOfStock={isOutOfStock}
            isInternational={isInternational}
          />
          {isSameSku && deliveryType && productTypeId !== productTypes.FINISHED_GOOD && (
            <>
              {borderLine()}
              <div className={`flex flex-col gap-12 ${isMobile ? "px-16" : ""}`} id="prodSwitchContainer">
                <h2 className="text-16 font-600 text-fnp-500">{constants.AVAILABLE_OPTIONS}</h2>
                <ChooseDeliveryType commonCleverTapData={commonCleverTapData} />
              </div>
            </>
          )}
          {borderLine()}

          {!isEmpty(serverVariantProducts) && (
            <>
              <div className={`${containerPadding}`}>
                <ComboComponents serverVariantProducts={serverVariantProducts} isMobile={isMobile} />
              </div>
            </>
          )}

          {!isOutOfStock && (
            <>
              {currentItem?.shippingDisplayOptions && currentItem?.shippingDisplayOptions?.showPincodeOrCity && (
                <div className={`${containerPadding}`} id="location_lock">
                  <Typography variant="p" Tag="p" classes={labelStyle}>
                    {constants.GIFT_RECEIVER_LOCATION}
                  </Typography>
                  {isMobile ? (
                    <MobileLocationLockSection
                      isLocationError={isNotServiceable}
                      fullProductDetails={fullProductDetails}
                      commonCleverTapData={commonCleverTapData}
                    />
                  ) : (
                    <DesktopLocationLockSection
                      isLocationError={isNotServiceable}
                      fullProductDetails={fullProductDetails}
                    />
                  )}
                </div>
              )}

              {borderLine()}

              {!isNotServiceable && (
                <>
                  <div className={`${containerPadding}`}>
                    <DeliveryDateAndtimeSlotSelection
                      currentItem={currentItem}
                      isMobile={isMobile}
                      selectedCurrency={selectedCurrency}
                    />
                  </div>
                  {borderLine()}
                </>
              )}

              {!isEmpty(get(currentItem, "personalizedInfo.personalizedOptions")) && (
                <>
                  <div className={`${containerPadding} flex flex-col gap-12`} id="personalization_section">
                    <PersonalizedOptionSection currentItem={currentItem} isMobile={isMobile} />
                  </div>
                  {borderLine()}
                </>
              )}

              {borderLine(true)}
            </>
          )}
          {!isEmpty(bannerData?.data) && (
            <>
              <div className={`${containerPadding}`} id="pdp-campaign-banners">
                <WebNativeDisplayBanners bannerData={bannerData} campaignEventData={campaignEventData} />
              </div>
              {borderLine()}
              {borderLine(true)}
            </>
          )}

          {!isEmpty(serverOffersData) && (
            <>
              <div className={`${containerPadding}`} id="offers-module-wrapper">
                <Typography variant="p" Tag="p" classes={`${labelStyle} xxl:text-18`}>
                  {constants.OFFERS_AVAILABLE}
                </Typography>
                <div className="flex flex-col gap-12">
                  {serverOffersData?.map((item) => {
                    return (
                      <OffersCard
                        customStyle={{
                          backgroundColor: item?.bgColor || "#0a4bff0a",
                          border: `1px solid ${item?.outline || "#0a4bffff"}`,
                        }}
                        imageSrc={item?.logo}
                        key={item?.logo}
                        content={item?.offerInfo}
                        tnc={item?.termsConditions}
                        isMobile={isMobile}
                        dimensions={item?.imgDimensions}
                      />
                    );
                  })}
                </div>
              </div>

              {borderLine()}
              {borderLine(true)}
            </>
          )}

          {!isEmpty(get(currentItem, "productContent")) && (
            <>
              <div className={`${containerPadding}`}>
                <AccordionSection currentItem={currentItem} />
              </div>
              {borderLine(true)}
            </>
          )}

          <BuyNowSection
            currentItem={currentItem}
            fullProductDetails={fullProductDetails}
            isMobile={isMobile}
            isNotServiceable={isNotServiceable}
            isOutOfStock={isOutOfStock}
            onButtonClick={onGa4EventBuyNow_AddToCart}
            commonCleverTapData={cleverTapPDPEventData}
          />
        </div>
      </div>

      <ZoomGallery
        productImages={getProductImages(currentItem)}
        productVideoLink={get(currentItem, "productContent.productVideoLink")}
        ctImageViewedData={pdpImageViewedData}
      />

      <ProductDetailWidgetSection
        currentItem={currentItem}
        isMobile={isMobile}
        serverCustomerReviewData={serverCustomerReviewData}
      />

      <CommonConditionalComponents isMobile={isMobile} commonCleverTapData={cleverTapPDPEventData} />

      {!isEmpty(pdpFooterLinks) && pdpFooterLinks?.mainList && (
        <ExploreMore data={pdpFooterLinks} isMobile={isMobile} />
      )}

      {isMobile && (
        <div className="px-16">
          <Breadcrumb items={createBreadcrumbsData(breadCrumb)} catalogId={catalogId} />
        </div>
      )}

      <TrustMetrics metrics={metrics} isMobile={isMobile} />
      <HiddenInputs
        productDetails={fullProductDetails}
        currentItem={currentItem}
        currencyCode={selectedCurrency?.currency?.currencyCode}
        deliveryDetailsData={deliveryDetailsData}
        isMobile={isMobile}
        fnpSalesChannelEnumId={
          typeof window !== "undefined" ? getFnpSalesChannelInfo(isMobile)?.fnpSalesChannelEnumId : ""
        }
      />
    </div>
  );
};
ProductDetailPage.propTypes = {
  serverData: PropTypes.object.isRequired,
  serverOffersData: PropTypes.array.isRequired,
  serverVariantProducts: PropTypes.array,
  serverVariantLabel: PropTypes.string,
  serverVariantType: PropTypes.string,
  serverVariantFlavor: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  serverSelectedCurrency: PropTypes.object,
  fullProductDetails: PropTypes.object.isRequired,
  breadCrumb: PropTypes.object,
  catalogId: PropTypes.string.isRequired,
  productStockAvailable: PropTypes.object.isRequired,
  pdpFooterLinks: PropTypes.object,
  serverCustomerReviewData: PropTypes.object.isRequired,
};
export default ProductDetailPage;
